<template>
  <modal :open="internalOpen" :width="1400">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t(`${tKey}.title`) }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <div class="flex flex-col gap-y-5 my-5 pb-5">
        <Multiselect
          v-model="rawData.driver"
          :error="errors?.driver"
          class="col-start-1 col-end-2"
          :options="driverList"
          :label="$t('campaignRegistration.driver')"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
          :min-chars="2"
          :filterResults="false"
          :delay="500"
          :resolve-on-load="true"
          :create-option="false"
          :clear-on-select="true"
          :no-options-text="$t('inputSearchString')"
        />
        <Multiselect
          v-model="rawData.type"
          :error="errors?.type"
          :options="possibleTypes"
          :label="$t('mountingTimeslotRegistration.type.DEFAULT')"
          :can-deselect="false"
          :can-clear="false"
        />
        <Multiselect
          v-model="rawData.campaignRegistration"
          :error="errors?.campaignRegistration"
          :label="$t('mountingTimeslotRegistration.campaign')"
          :options="campaignRegistrations"
          :can-deselect="false"
          :can-clear="false"
          :searchable="true"
          :loading="loadingCampaignRegistrations"
        />
        <text-area
          v-model="rawData.adminComment"
          :label="$t('mountingTimeslotRegistration.adminComment')"
          class="w-full"
          :disabled="!isAdmin"
        />
        <text-area
          v-model="rawData.comment"
          :error="errors?.comment"
          :label="$t('mountingTimeslotRegistration.comment')"
          class="w-full"
        />
      </div>
      <div class="w-full text-center">
        <custom-button
          variant="primary"
          class="mt-15"
          :busy="loading"
          @click="save"
        >
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import {
  collOptionsProvider,
  enumOptionsProvider,
  type ProviderParams,
} from '@utils/table/hooks/provider';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import { syncRef } from '@hooks/state';
import CustomButton from '@utils/CustomButton.vue';
import TextArea from '@utils/inputs/TextArea.vue';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import type {
  MountingTimeslotRegistration,
  Box,
  CampaignRegistration,
  Campaign,
  Driver,
  MountingTimeslot,
} from '@/entities/Entities';
import type { ByReference } from '@/utils/types';
import { useStore } from '@/store';

interface Props {
  open: boolean;
  mountingTimeslot: MountingTimeslot | null;
  mountingTimeslotRegistration?: MountingTimeslotRegistration;
}

const emit = defineEmits(['update:open', 'update:mountingTimeslotRegistration', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalMountingTimeslotRegistration = useVModel(props, 'mountingTimeslotRegistration', emit);

const isNew = computed(() => !props.mountingTimeslotRegistration);
const tKey = computed(() => `pages.mountings.mounting.mountingRegistrations.modal.edit${isNew.value ? '.new' : ''}`);

const store = useStore();
const isAdmin = store.getters['auth/IS_ADMIN'];

const { t } = useI18n();

const notify = useNotify();

const possibleTypes = enumOptionsProvider('/enums/MountingTimeslotRegistrationType', (item) => ({
  value: item,
  label: t(`mountingTimeslotRegistration.type.${item}`),
}));

const filter = computed(() => ({ driver: props.mountingTimeslotRegistration?.driver?.id }));

const campaignRegistrations = ref<Option[]>([]);
const loadingCampaignRegistrations = ref(false);

const driverList = (query: string, select: any) => {
  const input = query || select.modelValue;
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'user.fullName': input } : { id: select.modelValue },
  };
  return collOptionsProvider<Driver>('/drivers', filterQuery, (value) => ({
    label: value.user?.fullName,
    value: value['@id'],
  }))();
};

const campaignList = (query: string, select: any) => {
  const input = query || select.modelValue?.split('/')?.slice(-1)?.[0];
  if (!input) return Promise.resolve([]);
  const filterQuery: ProviderParams = {
    filter: query ? { 'group.name': input } : { id: input },
    sort: {
      sortKey: 'group.name',
      sortDirection: 'ASC',
    }
  };
  return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
    label: value.name,
    value: value['@id'],
  }))();
};

const loadCampaignRegistrations = async (id: string) => {
  loadingCampaignRegistrations.value = true;
  campaignRegistrations.value = await collOptionsProvider<CampaignRegistration>(
    '/campaign_registrations',
    {
      filter: { driver: id },
      sort: {
        sortKey: 'group.name',
        sortDirection: 'ASC',
      },
    },
    (value) => ({
      label: value.campaign.name,
      value: value['@id'],
    }),
  )();
  loadingCampaignRegistrations.value = false;
};

const initialBox: Partial<ByReference<Box, 'firstCampaign'>> = {
  mountingDate: undefined,
  type: undefined,
  sealNumber: undefined,
  status: undefined,
  firstCampaign: undefined,
  alreadyPayed: 0,
};

const initialData = (mountingTimeslotRegistration?: MountingTimeslotRegistration) =>
  (mountingTimeslotRegistration
    ? {
      ...mountingTimeslotRegistration,
      driver: mountingTimeslotRegistration.driver?.['@id'],
      campaignRegistration: mountingTimeslotRegistration?.campaignRegistration
        ? mountingTimeslotRegistration.campaignRegistration?.['@id']
        : undefined,
    }
    : {
      comment: undefined,
      adminComment: undefined,
      type: undefined,
      campaignRegistration: undefined,
      driver: undefined,
      box: initialBox,
    });

const rawData = syncRef(internalMountingTimeslotRegistration, (mountingTimeslotRegistration) => ({
  ...initialData(mountingTimeslotRegistration),
}));

const boxTypes = enumOptionsProvider('/enums/BoxType');

const {
  data, errors, validatedFn, setValidation,
} = useValidation(
  object({
    adminComment: string().nullable(),
    comment: string().nullable(),
    driver: string().required(t('validation.notEmpty')),
    type: string().nullable().required(t('validation.notEmpty')),
  }),
  rawData,
);

watch(
  () => rawData.value?.driver,
  (newVal) => {
    const id = newVal?.split('/').at(-1);
    if (id) {
      loadCampaignRegistrations(id);
    } else {
      campaignRegistrations.value = [];
    }
  },
);

function resetData() {
  rawData.value = { ...initialData() };
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    const edit = !!props.mountingTimeslotRegistration;
    const url = edit
      ? props.mountingTimeslotRegistration['@id']
      : '/mounting_timeslot_registrations';

    const timeslot = isNew.value
      ? props.mountingTimeslot?.['@id']
      : props.mountingTimeslotRegistration?.timeslot['@id'];

    if (!timeslot) throw new Error('No timeslot defined');

    await exec(url, {
      method: edit ? 'PUT' : 'POST',
      body: {
        timeslot,
        comment: data.value.comment,
        adminComment: data.value.adminComment,
        campaignRegistration: data.value.campaignRegistration,
        type: data.value.type,
        driver: data.value.driver,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    console.error(e);
    notify?.({
      variant: 'error',
      title: t('pages.mountings.mounting.mountingRegistrations.modal.edit.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
